import type { components } from '../types/api/generated';
import { ffhClient } from '@lib/api/ffh-client';
import { authHeader } from '@lib/api/auth-header';

export type MiniLeague =
  components['schemas']['LeagueListResponse']['data'][number];
export type LeagueDetail = NonNullable<
  components['schemas']['LeagueDetailResponse']['data']
>;
export type LeagueEntry = LeagueDetail['entries'][number];
export type LeagueStats = LeagueDetail['stats'];
export type LeagueStatsEntry = LeagueStats['ownership'][number];
export type LeagueEntryPlayer = LeagueEntry['starting'][number];
export type LiveTeam = NonNullable<
  components['schemas']['LiveTeamResponse']['data']
>;

export const fetchUserMiniLeagues = async (
  accessToken: string
): Promise<MiniLeague[]> => {
  const { data, error } = await ffhClient().GET('/live/leagues', {
    headers: authHeader(accessToken)
  });

  if (error) throw new Error('Failed to load mini leagues');
  return data?.data ?? [];
};

export const fetchMiniLeague = async (
  leagueId: number,
  accessToken: string
): Promise<LeagueDetail | null> => {
  const { data, error } = await ffhClient().GET('/live/leagues/{leagueId}', {
    headers: authHeader(accessToken),
    params: { path: { leagueId } }
  });

  if (error) throw new Error('Failed to load mini league');
  return data?.data ?? null;
};

/**
 * One member of a league with their squad. A league sent with
 * `hasSquads: false` carries no squads in its rows, so an opened row's squad
 * is read from here.
 */
export const fetchMiniLeagueEntry = async (
  leagueId: number,
  entryId: number,
  accessToken: string
): Promise<LeagueEntry | null> => {
  const { data, error } = await ffhClient().GET(
    '/live/leagues/{leagueId}/entries/{entryId}',
    {
      headers: authHeader(accessToken),
      params: { path: { leagueId, entryId } }
    }
  );

  if (error) throw new Error('Failed to load mini league entry');
  return data?.data ?? null;
};

export const fetchLiveTeam = async (
  accessToken: string,
  fplId?: number
): Promise<LiveTeam | null> => {
  const { data, error } = await ffhClient().GET('/live/team', {
    headers: authHeader(accessToken),
    params: fplId ? { query: { fplId } } : { query: {} }
  });

  if (error) throw new Error('Failed to load live team');
  return data?.data ?? null;
};
